/**
 * MIPS Performance Year Utilities
 *
 * The submission window for each performance year closes on April 1 00:00 UTC
 * of the following year, which equals March 31 at 8:00 PM EDT (UTC-4, DST active).
 *
 */

import { environment } from "@environments/environment";

/** The first year MIPS submissions are tracked in this application. */
export const MIPS_START_YEAR = 2025;

/**
 * TODO: TEMPORARY — global kill switch for DRCF signing, unscoped by
 * performance year. When false, this blocks DRCF signing (and therefore
 * submission) for every packet app-wide, including older packets that are
 * already fully signed. Sourced per-environment (dev/qa: enabled, prod:
 * disabled) — see `drcfSigningEnabled` in `src/environments/environment.*.ts`.
 * Remove this switch entirely once DRCF signing reopens for the season.
 */
export const DRCF_SIGNING_ENABLED = environment.drcfSigningEnabled;

export const REPORTING_TYPES = ["traditional", "mvp", "both"] as const;

export const MULTI_PERFORMANCE_TYPE = {
  SIMPLE_AVERAGE: 'S',
  WEIGHTED: 'W',
} as const;

export const PDMP_EXCLUSION_REQUIRED_MESSAGE =
  "When the e-Prescribing exclusion is claimed, a Query of PDMP exclusion must also be claimed.";

export function isPdmpExclusionMissing(
  performanceYear: number | null | undefined,
  claimedExclusionIds: string[],
): boolean {
  return (
    (performanceYear ?? 0) >= 2026 &&
    claimedExclusionIds.includes("PI_LVPP_1") &&
    !claimedExclusionIds.some(
      (exclusionId) => exclusionId === "PI_EP_2_EX_1" || exclusionId === "PI_EP_2_EX_2",
    )
  );
}

/**
 * Returns true after March 31 at 8 PM EDT (= April 1 00:00 UTC) of the current
 * calendar year, indicating the previous performance year's submission window is closed.
 */
export function isSubmissionsClosed(): boolean {
  const now = new Date();
  const cutoff = new Date(Date.UTC(now.getUTCFullYear(), 3, 1)); // April 1 00:00 UTC
  return now >= cutoff;
}

/**
 * Returns the currently active MIPS performance year.
 *   - Before April 1: returns (current year - 1)  e.g. Jan–Mar 2026 → 2025
 *   - After April 1:  returns current year         e.g. Apr–Dec 2026 → 2026
 */
export function getCurrentPerformanceYear(): number {
  const now = new Date();
  return isSubmissionsClosed() ? now.getUTCFullYear() : now.getUTCFullYear() - 1;
}

/**
 * Returns all available MIPS performance years from MIPS_START_YEAR through
 * the current performance year. Use this everywhere a year dropdown is needed
 * so the logic stays in one place.
 *
 * Examples:
 *   Before April 1 2026 → [2025]
 *   After  April 1 2026 → [2025, 2026]
 *   After  April 1 2027 → [2025, 2026, 2027]
 */
export function getAvailablePerformanceYears(): number[] {
  const current = getCurrentPerformanceYear();
  const years: number[] = [];
  for (let y = MIPS_START_YEAR; y <= current; y++) {
    years.push(y);
  }
  return years;
}

/**
 * Returns all available MIPS performance years for the dashboard, always
 * including the current calendar year regardless of whether the submission
 * window is open. This allows viewing historical data from any year at any time.
 *
 * Examples (before OR after April 1 2026): → [2025, 2026]
 */
export function getDashboardPerformanceYears(): number[] {
  const max = new Date().getUTCFullYear();
  const years: number[] = [];
  for (let y = MIPS_START_YEAR; y <= max; y++) years.push(y);
  return years;
}

/**
 * Returns the Jan 1 – Dec 31 date range for the current performance year.
 * Used for date picker bounds and validation in PI, IA, and effects.
 */
export function getMipsPerformanceDates() {
  const year = getCurrentPerformanceYear();
  return {
    MIN_DATE: new Date(year, 0, 1, 12, 0, 0),   // January 1
    MAX_DATE: new Date(year, 11, 31, 12, 0, 0),  // December 31
  };
}
